import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef, useState } from "react";
import SimpleBar from "simplebar-react";

import Carousel from "../components/Carousel";
import CustomiserSwitchList from "../components/CustomiserSwitchList";
import EllipsisLoader from "../components/EllipsisLoader";
import Selectable from "../components/Selectable";
import { NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { Facade, FacadeFinishOption } from "../types";
import { getPriceFormatted, scrollToTop } from "../util";
import Rendivo from "../util/rendivo";

const ChooseStreetAppealScreen_v2 = observer(() => {
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const [facades, setFacades] = useState<Facade[]>([]);

  const windowSize = useWindowSize();
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const optionsTopRef = useRef<HTMLDivElement>(null);
  const optionsBottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<SimpleBar>(null);
  const [scrollContainerHeight, setScrollContainerHeight] = useState(140); // Value for Mobile

  useEffect(() => {
    scrollToTop();
    // User is able to enter the app directly to this page - need to ensure the footer buttons are correct
    navContext.setFooterModePrimary();

    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.setSelectedFacade(null);
      userContext.resetSelectedFacadeFinishOption();
    });

    if (userContext.selectedHome) {
      setFacades(userContext.selectedHome.facades);
    }

    let selectedFacade = userContext.selectedFacade;
    if (
      !userContext.selectedFacade &&
      userContext.selectedHome &&
      userContext.selectedHome.facades.length
    ) {
      selectedFacade = userContext.selectedHome.facades[0];
    }

    if (selectedFacade) {
      onClickFacade(selectedFacade);
    }
  }, []);

  useEffect(() => {
    // Need to re-calculate the width of the tab scroll
    if (
      windowSize.width > 992 &&
      optionsContainerRef.current &&
      optionsTopRef.current &&
      optionsBottomRef.current &&
      scrollContainerRef.current
    ) {
      scrollContainerRef.current.el.style.maxHeight = "100%";
      const newHeight =
        optionsContainerRef.current.offsetHeight -
        optionsTopRef.current.offsetHeight -
        optionsBottomRef.current.offsetHeight -
        20;
      if (newHeight > 0) {
        setScrollContainerHeight(newHeight);
        scrollContainerRef.current.el.style.maxHeight = newHeight + "px";
      }
    } else {
      setScrollContainerHeight(140);
    }
  }, [
    optionsContainerRef.current,
    optionsTopRef.current,
    optionsBottomRef.current,
    scrollContainerRef.current,
    windowSize.width,
    userContext.selectedFacade,
  ]);

  useEffect(() => {
    navContext.setNextButtonEnabled(
      !!userContext.selectedFacade &&
        !!userContext.selectedFacadeFinishOption.get(
          userContext.selectedFacade.id
        )
    );
    navContext.setSubtotalVisible(userContext.subtotalPrice > 0);
  }, [userContext.selectedFacade, userContext.subtotalPrice]);

  const onClickFacade = (facade: Facade) => {
    let currentFinish: FacadeFinishOption | undefined;
    if (userContext.selectedFacade) {
      currentFinish = userContext.selectedFacadeFinishOption.get(
        userContext.selectedFacade.id
      );
    }

    userContext.setSelectedFacade(facade);

    // Search for a finish that matches the current finish's title
    let selectedFinish: FacadeFinishOption | undefined;
    if (currentFinish !== undefined) {
      const matchingFinish = facade.finish_options.filter(
        (item) =>
          currentFinish &&
          item.label.toLowerCase() === currentFinish.label.toLowerCase()
      );

      if (matchingFinish.length) {
        selectedFinish = matchingFinish[0];
        userContext.setSelectedFacadeFinishOption(facade.id, selectedFinish);
      }
    }

    if (!selectedFinish) {
      selectedFinish = userContext.selectedFacadeFinishOption.get(facade.id);
    }
    if (!selectedFinish) {
      const defaultFinished = facade.finish_options.filter(
        (item) => item.default
      );
      if (defaultFinished.length) {
        userContext.setSelectedFacadeFinishOption(
          facade.id,
          defaultFinished[0]
        );
      } else {
        userContext.setSelectedFacadeFinishOption(
          facade.id,
          facade.finish_options[0]
        );
      }
    }
  };

  const onClickFacadeFinishOption = (option: FacadeFinishOption) => {
    if (userContext.selectedFacade) {
      userContext.setSelectedFacadeFinishOption(
        userContext.selectedFacade.id,
        option
      );
    }
  };

  const onSlideCarousel = (index: number) => {
    onClickFacade(facades[index]);
  };

  const isFloorplanFlipped =
    !!userContext.selectedHome &&
    !!userContext.isFloorplanFlipped.get(userContext.selectedHome.id);

  const renderCarouselItems = (): JSX.Element[] => {
    return facades.map((facade) => {
      const selectedOption = userContext.selectedFacadeFinishOption.get(
        facade.id
      );
      return (
        <div className={"inner"} key={facade.id}>
          <EllipsisLoader />
          {facade.finish_options.map((option) => {
            return (
              <img
                key={facade.id + "_" + option.id}
                className={
                  selectedOption && selectedOption.id === option.id
                    ? "visible"
                    : "hidden"
                }
                src={Rendivo.getFacadeFinishRender(
                  1200,
                  facade.rendivo_id,
                  userContext.isCladdedSelected ? "CLADDED" : null,
                  option.rendivo_id,
                  isFloorplanFlipped
                )}
                srcSet={Rendivo.getFacadeFinishRenderSrcset(
                  1200,
                  facade.rendivo_id,
                  userContext.isCladdedSelected ? "CLADDED" : null,
                  option.rendivo_id,
                  isFloorplanFlipped
                )}
                sizes="(max-width: 1200px) 100vw, 1200px"
                alt={""}
              />
            );
          })}
        </div>
      );
    });
  };

  let selectedOption = null;
  if (userContext.selectedFacade) {
    selectedOption = userContext.selectedFacadeFinishOption.get(
      userContext.selectedFacade.id
    );
  }

  return (
    <div className={"screen screen-choose-street-appeal"}>
      <div className={"container"}>
        <h1>{"Choose Your Street Appeal"}</h1>
        <p className={"subtitle"}>
          Put your stamp on your new home with a facade option that suits your
          taste.
        </p>
        <p className={"section-label"}>
          Choose your Facade &amp; Facade Finish
        </p>
      </div>
      {userContext.selectedFacade && (
        <Selectable selected insetTick>
          <div className={"pyht-facade-finish-customiser-v2"}>
            <div
              className={`render-view ${
                !!userContext.selectedHome && userContext.selectedHome.acreage
                  ? "acreage"
                  : ""
              } ${
                !!userContext.selectedHome && userContext.selectedHome.narrow
                  ? "narrow"
                  : ""
              } ${
                userContext.selectedFacade.rendivo_levels > 1
                  ? "double-story"
                  : ""
              }`}
            >
              <Carousel
                onSlide={onSlideCarousel}
                itemWidthPerc={100}
                spacingPx={0}
                items={renderCarouselItems()}
              />
            </div>
            <div
              className={"facade-options-container"}
              key={userContext.selectedFacade.id}
            >
              <div className={"facade-options-inner"} ref={optionsContainerRef}>
                <div className={"options-top"} ref={optionsTopRef}>
                  <div className={"facade-details"}>
                    <div className={"title"}>
                      <h3>{userContext.selectedFacade.title}</h3>
                      <p>
                        {getPriceFormatted(
                          userContext.selectedFacade.base_price
                        )}
                      </p>
                    </div>
                  </div>
                  <p className={"section-label"}>Choose your Facade Finish</p>
                </div>
                <div className={"facade-options"}>
                  <div className={"facade-options-list"}>
                    <SimpleBar
                      autoHide={false}
                      ref={scrollContainerRef}
                      style={{ maxHeight: scrollContainerHeight }}
                    >
                      <CustomiserSwitchList
                        items={userContext.selectedFacade.finish_options}
                        selected={selectedOption ? [selectedOption] : []}
                        onClickOption={(option) =>
                          onClickFacadeFinishOption(
                            option as FacadeFinishOption
                          )
                        }
                      />
                    </SimpleBar>
                  </div>
                </div>
                <div className={"options-bottom"} ref={optionsBottomRef}>
                  <p className={"section-label"}>
                    Note: Choose your Facade Colours on the next step
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Selectable>
      )}
    </div>
  );
});

export default ChooseStreetAppealScreen_v2;
